import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../lib/api';

type Item = { to: string; label: string; icon: string; adminOnly?: boolean; systemOnly?: boolean };
type Section = {
  label: string;
  items: Item[];
  // Sections marked alwaysOpen don't collapse and don't show a chevron.
  // Used for Home (Dashboard only, no point collapsing).
  alwaysOpen?: boolean;
};

// Lifecycle-driven IA: Home → Plan → Build → Compliance → Finance → Settings.
// Each section maps to a phase in a BEAD subgrantee's lifecycle.
const sections: Section[] = [
  {
    label: 'Home',
    alwaysOpen: true,
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '◈' },
      // Projects link hidden — Dashboard's project picker + portfolio mode
      // covers the "browse all awards" use case. The legacy /projects URL
      // still works (Builder/As-built link to it directly), just no sidebar
      // entry to avoid the IA confusion of two different "Projects" views.
    ],
  },
  {
    label: 'Plan',
    items: [
      { to: '/project-economics', label: 'Project Economics', icon: '⊚' },
      { to: '/builder',           label: 'Builder',           icon: '⫫' },
      { to: '/plans-of-record',   label: 'Plans of Record',   icon: '✓' },
      { to: '/budgets',           label: 'Budgets',           icon: '¤' },
    ],
  },
  {
    label: 'Build',
    items: [
      { to: '/crews',        label: 'Crews',        icon: '👷' },
      { to: '/construction', label: 'Construction', icon: '🏗' },
      { to: '/as-built',     label: 'As-built',     icon: '◉' },
      // Legacy plant-graph view kept reachable but hidden from sidebar.
      // { to: '/built',     label: 'Built (plant)', icon: '◉' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/compliance',                    label: 'Overview',       icon: '◆' },
      { to: '/compliance/performance-measures', label: 'Performance Measures', icon: '⏱' },
      { to: '/permits',                       label: 'Permits',        icon: '📋' },
      // Reimbursements link hidden for now — page is a planning toy
      // (slider-driven actuals, not real QBO/BoM data) and the
      // story for Ready.net is "Reimbursement is Phase 2C, awaiting
      // your schema." A finished version will be a flagship feature
      // post-Ready.net partnership.
      // { to: '/bead-compliance/reimbursement', label: 'Reimbursements', icon: '🧾' },
      { to: '/bead-compliance/baba',          label: 'BABA Ledger',    icon: '⛓' },
      { to: '/bead-compliance/ehp',           label: 'EHP / NEPA',     icon: '🌿' },
      { to: '/bead-compliance/davis-bacon',   label: 'Davis-Bacon',    icon: '⚖' },
      { to: '/compliance/obligations',         label: 'Obligation Catalog', icon: '◆' },
      { to: '/compliance/grants',              label: 'Grant Assignments', icon: '💰' },
      { to: '/compliance/awards',              label: 'Grant Awards', icon: '🏆' },
      { to: '/compliance/makeready',           label: 'Make-Ready Risk', icon: '🌲' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { to: '/integrations/ready-net', label: 'Ready.net',         icon: '⇆' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/costs',           label: 'Pricing Overrides', icon: '$' },
      { to: '/settings/catalog', label: 'Rate Card',       icon: '¤' },
      { to: '/vendors',         label: 'Vendors',         icon: '⌘' },
      { to: '/procurement',     label: 'Procurement',     icon: '▦' },
      { to: '/invoices',        label: 'Invoices',        icon: '⎙' },
      { to: '/draws',           label: 'Draws',           icon: '↗' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/settings/system',       label: 'System Recovery', icon: '🛡', systemOnly: true },
      { to: '/settings/users',        label: 'Users',        icon: '👥', adminOnly: true },
      { to: '/settings/integrations', label: 'Integrations', icon: '⚡' },
      { to: '/settings/qbo-mapping',  label: 'QBO Mapping',  icon: '⇄' },
      { to: '/settings/deployment',   label: 'Deployment',   icon: '⌂' },
      { to: '/settings/sample-data',  label: 'Sample Data',  icon: '⚗' },
      { to: '/settings',              label: 'Settings',     icon: '⚙' },
    ],
  },
];

// v2: dropped auto-expand-on-active-route. Bumped so users on v1 with
// auto-expanded state from prior sessions get a fresh all-collapsed start.
const STORAGE_KEY = 'mb.sidebar.expanded.v2';

// Find which section contains a given route. Used to auto-expand the
// section the user is currently inside.
function sectionForPath(pathname: string): string | null {
  // Pick the longest matching item.to (so /settings/catalog beats /settings)
  let best: { sectionLabel: string; len: number } | null = null;
  for (const sec of sections) {
    for (const item of sec.items) {
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        if (!best || item.to.length > best.len) {
          best = { sectionLabel: sec.label, len: item.to.length };
        }
      }
    }
  }
  return best?.sectionLabel ?? null;
}

function loadExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export default function Sidebar() {
  const location = useLocation();
  const activeSection = sectionForPath(location.pathname);

  // Initial state: pure localStorage. No auto-expand on the active route —
  // demo audiences want to see the full IA (6 collapsed buckets) at a glance,
  // not a partially-expanded menu that changes shape per page. The blue
  // dot on a collapsed section still flags "active page is in here."
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => loadExpanded());

  // Pull the current user's role so we can hide admin-only nav entries from
  // non-admin viewers. Without this, a Field crew member would see a "Users"
  // link that bounces them away on click — confusing UX.
  const { data: me } = useQuery<{ role?: string; is_system?: boolean }>({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
    staleTime: 60_000,
  });
  const isAdmin = me?.role === 'admin';
  const isSystem = !!me?.is_system;

  // Persist user toggles.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded)); } catch {}
  }, [expanded]);

  const toggle = (label: string) =>
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="sidebar__brand-mark">M</span>
        <span>Milestone <b style={{ fontWeight: 800 }}>Build</b></span>
      </div>

      {sections.map((section) => {
        const isOpen = section.alwaysOpen || !!expanded[section.label];
        const isActiveSection = activeSection === section.label;
        return (
          <div key={section.label}>
            {section.alwaysOpen ? (
              <div className="sidebar__section-label">{section.label}</div>
            ) : (
              <div
                className="sidebar__section-label"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggle(section.label)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(section.label);
                  }
                }}
                style={{
                  // Only layout overrides — let the .sidebar__section-label
                  // CSS class own all font/case/tracking/color/padding so
                  // collapsed headings look identical to the always-open
                  // HOME heading.
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span>
                  {section.label}
                  {!isOpen && isActiveSection && (
                    <span
                      title="Active page is inside this section"
                      style={{
                        display: 'inline-block', width: 6, height: 6, borderRadius: 999,
                        background: '#60a5fa', marginLeft: 6, verticalAlign: 'middle',
                      }}
                    />
                  )}
                </span>
                <span style={{
                  fontSize: 10, opacity: 0.7,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                  display: 'inline-block',
                }}>▸</span>
              </div>
            )}

            {isOpen && (
              <nav className="sidebar__nav" style={{ marginBottom: 12 }}>
                {section.items
                  .filter(item => !item.adminOnly || isAdmin)
                  .filter(item => !item.systemOnly || isSystem)
                  .map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/settings'}  /* /settings shouldn't match every /settings/* */
                    className={({ isActive }) => clsx('sidebar__link', isActive && 'active')}
                  >
                    <span className="sidebar__icon" aria-hidden>{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            )}
          </div>
        );
      })}

      <div className="sidebar__footer">
        v0.1.0 · build {new Date().getFullYear()}
      </div>
    </aside>
  );
}
